import React, {useCallback, useContext, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {BuilderContext} from '@builder.io/sdk-react'

// Hooks
import useLearningCenterMutation from 'hec/hooks/learning-center/useLearningCenterMutation'
import useLearningCenterModule from './useLearningCenterModule'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

// Components
import {Heading as HecHeading} from '@/components/ui/heading'
import {BodyCopy as HecBodyCopy} from '@/components/ui/body-copy'
import ModuleAccordion from './moduleAccordion'
import ModuleProgress from './moduleProgress'
import {useToast} from '@/hooks/useToast'
import {useIntl} from 'react-intl'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'

// Other
import {isPreviewing} from '@builder.io/sdk-react'
import {
    hasGuestLearningCenterData,
    getGuestLearningData
} from '../../../../overrides/app/utils/helpers'

export const LearningCenterModule = ({attributes}) => {
    const {data: customer} = useCurrentCustomer()
    const {customerType} = customer
    const isGuest = customerType === 'guest'
    const {openGoal, startModule, startLevel} = useLearningCenterMutation()
    const [requestInProgress, setRequestInProgress] = useState(false)
    const {formatMessage} = useIntl()

    const showToast = useToast()
    const showError = useCallback(() => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    })

    const builderContext = useContext(BuilderContext)
    const {module: moduleRef} = builderContext.content.data || {}
    const setGoal = useCallback(async () => {
        setRequestInProgress(true)
        if (moduleRef.value && moduleRef.value.name) {
            const moduleName = moduleRef.value.name
            const goalId = moduleName.split('--')[0]
            const response = await openGoal(goalId)
            if (!response.success) {
                console.log('Error opening goal:', response.errorMsg)
                showError(response.errorMsg)
            }
        }
        setRequestInProgress(false)
    }, [openGoal, showError, moduleRef.value])

    const setLevel = useCallback(async () => {
        setRequestInProgress(true)
        if (moduleRef.value && moduleRef.value.name) {
            const moduleName = moduleRef.value.name
            const goalId = moduleName.split('--')[0]
            const levelId = moduleName.split('--').slice(0, 2).join('--')
            const newData = getGuestLearningData()
            startLevel(levelId, goalId, newData)
            const response = await startModule(moduleName, levelId, newData)
            if (!response.success) {
                console.log('Error starting module:', response.errorMsg)
                showError(response.errorMsg)
            }
        }
        setRequestInProgress(false)
    }, [moduleRef.value, startLevel, startModule, showError])

    useEffect(() => {
        if (isGuest && !hasGuestLearningCenterData()) {
            setGoal()
            setLevel()
        }
    }, [isGuest, setGoal, setLevel])

    useEffect(() => {
        if (isGuest) {
            showToast({
                title: formatMessage({id: 'Guest users have a two lesson limit'}),
                status: 'info',
                position: 'bottom'
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const moduleData = useLearningCenterModule(moduleRef)
    const {module, componentIsLoading, isError} = moduleData

    if (componentIsLoading) {
        return <div>Loading...</div>
    }

    if (isError) {
        return <div>There was an error</div>
    }

    if (!module) return <div>Please select a module</div>

    return (
        (isPreviewing() ? true : !!module) && (
            <div {...attributes}>
                <div className="px-2 lg:px-6 mb-6 lg:mb-8">
                    <HecHeading
                        tag="h3"
                        variant="h3"
                        className="mb-1"
                        data-testid="learningCenterModule-HecHeading"
                    >
                        {module.title}
                    </HecHeading>
                    <HecBodyCopy
                        size="large"
                        className="mb-3 lg:mb-6"
                        data-testid="learningCenterModule-HecBodyCopy"
                    >
                        Review lessons and pass quiz in order to earn points
                    </HecBodyCopy>
                    <ModuleProgress
                        lessons={module.lessons}
                        quiz={module.quiz}
                        data-testid="learningCenterModule-ModuleProgress"
                    />
                    <ModuleAccordion
                        moduleData={moduleData}
                        data-testid="learningCenterModule-ModuleAccordion"
                    />
                </div>
            </div>
        )
    )
}

LearningCenterModule.propTypes = {
    moduleRef: PropTypes.any,
    attributes: PropTypes.object
}

export const LearningCenterModuleDefinition = {
    component: LearningCenterModule,
    name: 'Learning Center Module',
    noWrap: true
}
